/**
 * Match Suggestion — Find ART_INCOME transactions that could match a sold verdict.
 *
 * Used by Flow C (match during mark-as-sold) and potentially Flow A (reverse match).
 *
 * Match criteria:
 * - tpCategory === 'ART_INCOME'
 * - tpLinkedArtworkId is null (not already linked)
 * - Amount within 40%-110% of verdictSoldPrice (galleries take 10-50% commission)
 * - Date within ±14 days of verdict soldDate
 */

import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@/firebase/config';

export interface MatchCandidate {
  transactionId: string;
  merchantName: string;
  amount: number;              // cents (absolute value)
  date: string;                // YYYY-MM-DD
  amountMatchPercent: number;  // how close to verdict soldPrice (100 = exact)
  daysApart: number;           // abs days between txn date and sold date
}

export async function findMatchCandidates(
  userId: string,
  verdictSoldPriceCents: number,
  verdictSoldDate: string,
): Promise<MatchCandidate[]> {
  const soldDate = verdictSoldDate.substring(0, 10);
  const soldMs = new Date(soldDate).getTime();

  // Date window: ±14 days
  const startDate = new Date(soldMs - 14 * 86400000).toISOString().substring(0, 10);
  const endDate = new Date(soldMs + 14 * 86400000).toISOString().substring(0, 10);

  const txnRef = collection(db, `users/${userId}/transactions`);
  const q = query(
    txnRef,
    where('tpCategory', '==', 'ART_INCOME'),
    where('pending', '==', false),
    where('date', '>=', startDate),
    where('date', '<=', endDate),
    orderBy('date', 'desc'),
  );

  const snap = await getDocs(q);
  const candidates: MatchCandidate[] = [];

  for (const d of snap.docs) {
    const txn = d.data();

    // Skip already-linked transactions
    if (txn.tpLinkedArtworkId) continue;

    const txnAmountCents = Math.abs(txn.amount);

    // Amount tolerance: 40% to 110% of verdict price
    const ratio = txnAmountCents / verdictSoldPriceCents;
    if (ratio < 0.4 || ratio > 1.1) continue;

    const daysApart = Math.abs(
      Math.round((new Date(txn.date).getTime() - soldMs) / 86400000)
    );

    candidates.push({
      transactionId: d.id,
      merchantName: txn.merchantName || txn.name || 'Unknown',
      amount: txnAmountCents,
      date: txn.date,
      amountMatchPercent: Math.round(ratio * 100),
      daysApart,
    });
  }

  // Sort by quality: closest amount ratio to 100%, then fewest days apart
  candidates.sort((a, b) => {
    const aQuality = Math.abs(100 - a.amountMatchPercent) + a.daysApart * 2;
    const bQuality = Math.abs(100 - b.amountMatchPercent) + b.daysApart * 2;
    return aQuality - bQuality;
  });

  return candidates;
}
